import React, { useEffect, useState } from 'react';
import Alerts from '../components/Alerts';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addCompanyId, addStoreId } from '../store/setupSlice';

interface CompanyIdResponse {
  succeded: boolean;
  message: string;
  data?: {
    id?: number;
  };
  responseCode: string;
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

const getPathSegments = (pathname: string) =>
  pathname.split('/').filter(Boolean);

const getNumericSegment = (segment?: string | null) => {
  if (!segment) {
    return undefined;
  }

  const numericValue = Number(segment);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const getSlugOrDomain = () => {
  const normalizedHostname = window.location.hostname.toLowerCase();
  const configuredRootDomain = import.meta.env.VITE_ROOTDOMAIN?.toLowerCase();

  if (configuredRootDomain) {
    const expectedSuffix = `.${configuredRootDomain}`;
    if (!normalizedHostname.endsWith(expectedSuffix)) {
      return normalizedHostname;
    }
  }

  const hostnameParts = normalizedHostname.split('.').filter(Boolean);
  const slug = hostnameParts[0];

  return slug && slug !== 'www' ? slug : normalizedHostname;
};

const fetchCompanyId = async (customDomainOrSlug: string) => {
  try {
    const url = new URL(`${import.meta.env.VITE_BASEURL}company/id`);
    const numericCompanyId = getNumericSegment(customDomainOrSlug);
    if (typeof numericCompanyId !== 'undefined') {
      return numericCompanyId;
    }
    url.searchParams.set('customDomainOrSlug', customDomainOrSlug);

    const response = await fetch(url.toString());
    if (!response.ok) {
      return undefined;
    }

    const result: CompanyIdResponse = await response.json();
    const responseId = result.data?.id;

    if (!result.succeded || typeof responseId === 'undefined') {
      return undefined;
    }

    const companyId = Number(responseId);
    return Number.isFinite(companyId) ? companyId : undefined;
  } catch {
    return undefined;
  }
};

const withGetCompanyID = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  const WithCompanyID: React.FC<P> = (props) => {
    const dispatch = useDispatch();
    const pathname = useLocation().pathname; // e.g. "/8329/some/route"
    const [hostname] = useState(() => window.location.hostname.toLowerCase());
    // undefined = still loading, number = resolved, null = failed to resolve
    const [resolvedCompanyId, setResolvedCompanyId] = useState<
      number | null | undefined
    >(undefined);

    useEffect(() => {
      let isMounted = true;

      const resolveCompanyId = async () => {
        let storeIdNum: number | undefined;

        const segments = getPathSegments(pathname);

        let slug = '';
        if (LOCAL_HOSTS.has(window.location.hostname)) {
          slug = segments[0];
          storeIdNum = getNumericSegment(segments[1]);
        } else {
          slug = getSlugOrDomain();
          storeIdNum = getNumericSegment(segments[0]);
        }
        const companyIdNum: number | undefined = await fetchCompanyId(slug);
        if (storeIdNum) {
          dispatch(addStoreId(storeIdNum));
        }
        if (!isMounted) {
          return;
        }

        if (companyIdNum) {
          setResolvedCompanyId(companyIdNum);
          dispatch(addCompanyId(companyIdNum));
          localStorage.setItem('companyId', companyIdNum.toString());
        } else {
          setResolvedCompanyId(null);
          localStorage.removeItem('companyId');
        }
      };

      resolveCompanyId();

      return () => {
        isMounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, hostname]); // hostname is stable during SPA navigation; pathname intentionally omitted

    return (
      <>
        {typeof resolvedCompanyId === 'number' && (
          <WrappedComponent {...(props as P)} companyID={resolvedCompanyId} />
        )}
        {resolvedCompanyId === undefined && <Alerts message="Loading ..." />}
        {resolvedCompanyId === null && (
          <Alerts message="Unable to determine the company from the URL or domain. Please ensure the URL contains a valid company/store identifier or that your slug/custom domain is correctly configured." />
        )}
      </>
    );
  };

  WithCompanyID.displayName = `withGetCompanyID(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithCompanyID;
};

export default withGetCompanyID;
